// PROJECTION_CHOKEPOINT — co-located with project() so the player-WS
// Viewer-set construction (III.B.4 / #68) reads through the same op-log
// surface (`ReadOnlyStore`) the projection chokepoint reads through.

import {
  type FactPayload,
  FactPayloadSchema,
  factKey,
  foldFactChainFull,
  OFFICIAL_CORE_PREDICATE_IDS,
} from '@dl/shared';

import { type ReadOnlyStore } from './store.js';

/** Minimal op shape returned by `releaseControllerBindings`. */
export interface ControllerReleaseOp {
  subject: string;
  predicate: string;
  payload: { kind: 'fact.soft_delete' };
}

/**
 * The system "controller" predicate. Post-ADR-0004 (#178) predicate ids are
 * opaque + immutable, so this is the official-core `controlled_by` MINTED id —
 * NOT the legacy `'controlled_by'` token string (matching the string here was a
 * stale-identity bug: facts are written under the opaque id, so the string
 * matched nothing and every controlling User resolved to zero Characters).
 */
const CONTROLLED_BY_PREDICATE = OFFICIAL_CORE_PREDICATE_IDS.controlled_by;

/**
 * III.B.4 (#68) — Resolve the set of Character entity ids a given User
 * `controlled_by` in this Campaign's op log.
 *
 * Convention: a `controlled_by` fact on a Character subject carries the
 * controlling User's id as its text value. Soft-deleted chains are
 * excluded — relinquishing control by emitting `fact.soft_delete`
 * immediately drops the Character from the User's effective Viewer set.
 *
 * The controller predicate id is overridable (`controllerPredicate`) so the
 * resolver stays pack-agnostic; it defaults to official-core `controlled_by`.
 *
 * O(ops) time. Returns the Character ids sorted for determinism.
 */
export function resolveControlledCharacters(
  store: ReadOnlyStore,
  userId: string,
  controllerPredicate: string = CONTROLLED_BY_PREDICATE,
): string[] {
  const chains = new Map<string, { subject: string; payloads: FactPayload[] }>();
  for (const op of store.iterateOps()) {
    if (op.predicate !== controllerPredicate) continue;
    const payload = op.payload as { kind?: unknown };
    if (!payload || typeof payload.kind !== 'string' || !payload.kind.startsWith('fact.')) {
      continue;
    }
    const parsed = FactPayloadSchema.safeParse(op.payload);
    if (!parsed.success) continue;
    const key = factKey(op.subject, op.predicate);
    const existing = chains.get(key);
    if (existing) existing.payloads.push(parsed.data);
    else chains.set(key, { subject: op.subject, payloads: [parsed.data] });
  }

  const out = new Set<string>();
  for (const { subject, payloads } of chains.values()) {
    const folded = foldFactChainFull(payloads);
    if (!folded || folded.soft_deleted) continue;
    // Authorization must never ride on unratified data. A `controlled_by` fact at
    // `draft`/`hypothetical`/`pending` status (e.g. LLM-authored or mid-conflict)
    // must NOT expand the user's effective Viewer set — that would hand a player a
    // Character's perspective and private beliefs before the GM ratifies. Only a
    // canon binding grants control. ("The LLM drafts; the GM ratifies.")
    if (folded.status !== 'canon') continue;
    if (folded.value === null) continue;
    if (typeof folded.value !== 'string') continue;
    if (folded.value !== userId) continue;
    out.add(subject);
  }
  return Array.from(out).sort();
}

/**
 * #264 / #267 — Departure-hook helper: produce `fact.soft_delete` ops for
 * every `controlled_by` chain where the folded value equals `userId`.
 *
 * Called by the worker's member-remove path (#267) BEFORE deleting the
 * `campaign_members` row, so the DO can append the releases in the same
 * request turn before the member loses access.
 *
 * Returns one `ControllerReleaseOp` per controlled Character, sorted by
 * `subject` for determinism. Already-soft-deleted chains are excluded — they
 * are already released and a redundant soft-delete would create a spurious op.
 *
 * The caller must submit these through `appendOp` / the DO's `appendAndArbitrate`
 * path (or the GM WS channel) so the op log stays the source of truth.
 *
 * The controller predicate is overridable (`controllerPredicate`) for the same
 * pack-agnostic reason as in `resolveControlledCharacters`.
 */
export function releaseControllerBindings(
  store: ReadOnlyStore,
  userId: string,
  controllerPredicate: string = CONTROLLED_BY_PREDICATE,
): ControllerReleaseOp[] {
  const controlled = resolveControlledCharacters(store, userId, controllerPredicate);
  return controlled.map((subject) => ({
    subject,
    predicate: controllerPredicate,
    payload: { kind: 'fact.soft_delete' as const },
  }));
}
